<script setup>
import { ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import { useQrCodeAutoRefresh } from 'dashboard/composables/useQrCodeAutoRefresh';
import NextButton from 'dashboard/components-next/button/Button.vue';
import QrCodeAutoRefreshIndicator from 'dashboard/components/QrCodeAutoRefreshIndicator.vue';

const STATUS_POLL_INTERVAL_MS = 3000;

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const step = ref('form');
const inboxName = ref('');
const serverUrl = ref('');
const adminApiKey = ref('');
const phoneNumber = ref('');

const isSubmitting = ref(false);
const isPolling = ref(false);
const instanceName = ref('');
let pollTimer = null;

const v$ = useVuelidate(
  {
    inboxName: { required },
    serverUrl: { required, url },
    adminApiKey: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
  },
  { inboxName, serverUrl, adminApiKey, phoneNumber }
);

const {
  qrcodeBase64,
  isLoadingQrcode,
  secondsRemaining,
  needsManualRefresh,
  totalSeconds,
  start: startQrAutoRefresh,
  stop: stopQrAutoRefresh,
  manualRefresh: refreshQrcode,
} = useQrCodeAutoRefresh(async () => {
  const { data } = await whatsappChannel.evolutionInstanceQrcode({
    serverUrl: serverUrl.value,
    apiKey: adminApiKey.value,
    instanceName: instanceName.value,
  });
  return data.qrcode_base64;
});

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  isPolling.value = false;
};

const finalizeChannel = async resolvedPhoneNumber => {
  try {
    const evolutionChannel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: `+${resolvedPhoneNumber}`,
        provider: 'evolution',
        provider_config: {
          server_url: serverUrl.value,
          instance_name: instanceName.value,
          api_key: adminApiKey.value,
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: evolutionChannel.id },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE')
    );
  }
};

const pollStatus = async () => {
  try {
    const { data } = await whatsappChannel.evolutionInstanceStatus({
      serverUrl: serverUrl.value,
      apiKey: adminApiKey.value,
      instanceName: instanceName.value,
      phoneNumber: phoneNumber.value,
    });

    // The backend already unpaired that phone; the QR keeps refreshing for the right one.
    if (data.state === 'number_mismatch') {
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.WIZARD', {
          scanned: `+${data.phone_number}`,
          expected: phoneNumber.value,
        })
      );
      return;
    }

    if (data.state === 'open' && data.phone_number) {
      stopPolling();
      stopQrAutoRefresh();
      await finalizeChannel(data.phone_number);
    }
  } catch (error) {
    // Transient network hiccups shouldn't abort the wizard - just keep polling.
  }
};

const startPolling = () => {
  isPolling.value = true;
  pollTimer = setInterval(pollStatus, STATUS_POLL_INTERVAL_MS);
};

const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  isSubmitting.value = true;
  try {
    // Backend finds an existing instance already connected to this number and reuses it,
    // or creates a new one - either way it reports the current state back.
    const { data } = await whatsappChannel.createEvolutionInstance({
      serverUrl: serverUrl.value,
      adminApiKey: adminApiKey.value,
      phoneNumber: phoneNumber.value,
    });

    instanceName.value = data.instance_name;

    if (data.state === 'open' && data.phone_number) {
      await finalizeChannel(data.phone_number);
      return;
    }

    step.value = 'qr';
    await startQrAutoRefresh(data.qrcode_base64);
    startPolling();
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div v-if="step === 'form'" class="flex flex-wrap flex-col mx-0">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="submitForm">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.serverUrl.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SERVER_URL.LABEL') }}
          <input
            v-model="serverUrl"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SERVER_URL.PLACEHOLDER')
            "
            @blur="v$.serverUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SERVER_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.adminApiKey.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.ADMIN_API_KEY.LABEL') }}
          <input
            v-model="adminApiKey"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.ADMIN_API_KEY.PLACEHOLDER')
            "
            @blur="v$.adminApiKey.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.ADMIN_API_KEY.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="v$.phoneNumber.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PHONE_NUMBER.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>

  <div v-else class="flex flex-col items-center gap-4 py-4 text-center">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.DESC') }}
    </p>
    <img
      v-if="qrcodeBase64"
      :src="qrcodeBase64"
      :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.TITLE')"
      class="size-56 rounded-lg border border-n-weak"
    />
    <p v-if="isPolling" class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.WAITING') }}
    </p>
    <QrCodeAutoRefreshIndicator
      :seconds-remaining="secondsRemaining"
      :total-seconds="totalSeconds"
      :needs-manual-refresh="needsManualRefresh"
      :is-loading="isLoadingQrcode"
      @refresh="refreshQrcode"
    />
  </div>
</template>
